import React from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import errorReporter from '@app/errorReporter';
import { AuthContext } from 'contexts';
import { Button, ContentList, Page, generateConvoItem } from 'components';
import { PopulatedUser } from 'api/UserController/getProfile';
import { useUserMessages, MessageSearchState, SelectedRep } from './hooks';
import MessageSearchBar from './MessageSearchBar';

const StyledInboxContainer = styled.div`
  background-color: white;
  padding: 20px;
`;

const StyledLoadMore = styled.div`
  text-align: center;
`;

const MessagingInboxPage: React.FC = () => {
  const { convoId, userId } = useParams<Record<string, string>>();
  const { hasFeature } = React.useContext(AuthContext);
  const [searchState, setSearchState] = React.useState<MessageSearchState>({
    text: '',
  });
  const [userInfo, setUserInfo] = React.useState<SelectedRep | undefined>();

  const {
    messagingAuth,
    conversations,
    isLoading,
    hasMore,
    orgNames,
    isValidExport,
    getConvos,
    loadMoreConvos,
  } = useUserMessages(searchState, userId);

  const LoadMoreComponent = React.useMemo(
    () => (
      <StyledLoadMore>
        <Button
          onClick={loadMoreConvos}
          disabled={!hasMore || isLoading}
          loading={isLoading}
        >
          {hasMore ? 'Load More' : 'No More Conversations'}
        </Button>
      </StyledLoadMore>
    ),
    [isLoading, hasMore, loadMoreConvos],
  );

  const onConvoItemClick = (conversationId: string) => {
    axios.get(`/messaging/openConvo/${conversationId}`);
  };

  React.useEffect(() => {
    if (convoId) onConvoItemClick(convoId);
  }, [convoId]);

  React.useEffect(() => {
    if (userId) {
      const fetchUserInfo = async () => {
        try {
          const { data } = await axios.get<PopulatedUser>(
            `/user/${userId}/profile`,
          );
          setUserInfo(data);
        } catch (err) {
          errorReporter.error(err);
        }
      };
      fetchUserInfo();
      return () => setUserInfo(undefined);
    }
  }, [userId]);

  if (!hasFeature('CHAT') || !messagingAuth || (userId && !userInfo)) {
    return null;
  }

  return (
    <Page title="Message Inbox" type="hollow" fillScreenHeight>
      <StyledInboxContainer>
        <MessageSearchBar
          searchState={searchState}
          userInfo={userInfo}
          isValidExport={isValidExport}
          getConvos={getConvos}
          setSearchState={setSearchState}
        />
        <ContentList
          items={conversations.map(convo =>
            generateConvoItem({
              convo,
              me: messagingAuth,
              showDate: true,
              orgName: orgNames[String(convo.assumedBrokerageId)],
            }),
          )}
          isLoading={isLoading && !conversations.length}
          emptyDescription="You have no Messages."
          loadMore={LoadMoreComponent}
        />
      </StyledInboxContainer>
    </Page>
  );
};

export default MessagingInboxPage;
